import React, { useCallback } from 'react';
import { useRouter } from 'next/router';
import { GetStaticPropsContext } from 'next';
import { useTranslation } from 'react-i18next/';
import Head from 'next/head';

import styles from './Bestemmingen.module.scss';
import PrimaryHeaderProps from '@components/ui/PrimaryHeader/PrimaryHeader.props';
import ContinentMenuProps from '@components/navigation/ContinentMenu/ContinentMenu.props';
import { Meta } from '@components/layout/Meta/Meta';
import CategoryOverview from '@components/lists/CategoryOverview/CategoryOverview';
import ContinentMenu from '@components/navigation/ContinentMenu/ContinentMenu';
import PrimaryHeader from '@components/ui/PrimaryHeader/PrimaryHeader';
import GraphqlService from '@services/GraphqlService';
import PersonalService from '@services/PersonalService';
import GenericService from '@services/GenericService';
import MailchimpService from '@services/MailchimpService';
import { CategoryOverviewModule } from 'modules/CategoryOverviewModule/CategoryOverviewModule.service';
import { EN_DOMAIN, FB_APP_ID, NL_DOMAIN, REVALIDATION_TIME } from '@constants/config';
import { getRoute } from '@constants/routes';
import { PageProps } from '@constants/types';
import { DestinationLinksProvider } from '@hooks/useDestinationLinks';
import { SiteLinksProvider } from '@hooks/useSiteLinks';
import { FooterLinksProvider } from '@hooks/useFooterLinks';
import Layout from '@components/layout/Layout/Layout';
import { NavbarTabsTypes } from '@constants/enums';
import CookieDialog from '@components/forms/CookieDialog/CookieDialog';
import {
  ICategoryOverviewArchivePublicationFields,
  ICategoryOverviewPublicationFields,
} from 'modules/CategoryOverviewModule';
import { useLocale } from '@hooks/useLocale';

const { FacebookProvider } = require('react-facebook');

interface Props extends PageProps<ICategoryOverviewArchivePublicationFields> {
  categoryOverviews: ICategoryOverviewPublicationFields[];
}

const DestinationsPage = ({
  fields,
  siteLinks,
  destinationLinks,
  footerLinks,
  categoryOverviews,
}: Props) => {
  const router = useRouter();
  const locale = useLocale();
  const { t } = useTranslation();
  const { active } = router.query;

  const getFullPath = () => {
    const isDP = locale === 'nl-NL';
    const host = isDP ? NL_DOMAIN : EN_DOMAIN;
    const slug = getRoute(locale || 'nl-NL', 'DESTINATIONS');

    return host + slug + '/';
  };

  const currentOverview = categoryOverviews.find((co) => co._slug === active);

  const getCategories = useCallback(() => {
    return fields.categories.filter((item) => {
      return item.parent === active || !active;
    });
  }, [fields.categories, active]);

  const getHeaderData = useCallback((): PrimaryHeaderProps => {
    const source = currentOverview || fields;

    return {
      title: source.header_title,
      description: source.header_subtitle,
      backgroundImage: source.thumbnail?.[0].cdn_files[0]?.full,
    };
  }, [currentOverview, fields]);

  const getContinentMenuData = useCallback((): ContinentMenuProps => {
    const result = categoryOverviews.map((co) => ({
      href: `/${fields._slug}?active=${co._slug}`,
      children: co.title,
      selected: active === co._slug,
    }));

    result.unshift({
      href: `/${fields._slug}`,
      children: t('DOMAIN.DESTINATIONS.ALL'),
      selected: !active,
    });

    return { continents: result || [] };
  }, [categoryOverviews, active]);

  return (
    <FacebookProvider appId={FB_APP_ID} xfbml>
      <Head>
        <link rel="canonical" href={getFullPath()} />
      </Head>

      <FooterLinksProvider value={footerLinks}>
        <DestinationLinksProvider value={destinationLinks}>
          <SiteLinksProvider value={siteLinks}>
            <Layout selectedTab={NavbarTabsTypes.DESTINATIONS} paddings={false}>
              <div
                className={styles.CategoryOverviewArchivePage}
                data-testid="CategoryOverviewArchivePage">
                <Meta
                  title={fields.meta_title || fields.title}
                  description={fields.meta_description || fields.header_subtitle}
                  image={fields.thumbnail?.[0]?.cdn_files[0]?.full}
                />
                <PrimaryHeader {...getHeaderData()} />
                <ContinentMenu {...getContinentMenuData()} />
                <div className="container">
                  {!!fields.categories && (
                    <CategoryOverview
                      categories={getCategories()}
                      className={styles.CategoryOverview}
                      removeDoubles
                    />
                  )}
                </div>
              </div>
            </Layout>
            <CookieDialog />
          </SiteLinksProvider>
        </DestinationLinksProvider>
      </FooterLinksProvider>
    </FacebookProvider>
  );
};

export default DestinationsPage;

export const getStaticProps = async ({ locale, preview }: GetStaticPropsContext) => {
  GraphqlService.setLocale(locale);
  PersonalService.setLocale(locale);
  GenericService.setLocale(locale);
  MailchimpService.setLocale(locale);

  GraphqlService.setPreview(!!preview);

  const publication = await CategoryOverviewModule.getArchive(
    locale === 'nl-NL' ? 'bestemmingen' : 'destinations',
  );

  if (!publication) {
    return {
      notFound: true,
      revalidate: REVALIDATION_TIME,
    };
  }

  return {
    props: publication,
    revalidate: REVALIDATION_TIME,
  };
};
